import React from 'react';
import { fmtMoney } from '../lib/format';
import { BillingType, ClientBilling } from '../types/superadmin';

type Billing = ClientBilling;

// chip tone + human label per billing classification.
const TYPE_META: Record<BillingType, { tone: string; label: string }> = {
  trial: { tone: 'warn', label: 'Trial / comp access' },
  paying_full: { tone: 'pos', label: 'Paying full price' },
  discounted: { tone: 'warn', label: 'Discounted (coupon)' },
  free_plan: { tone: 'warn', label: 'Free plan ($0 list)' },
  past_due: { tone: 'neg', label: 'Past due' },
  canceled: { tone: 'neg', label: 'Canceled' },
  none: { tone: '', label: 'No subscription' }
};

/** Small labeled money/stat cell. */
function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="card">
      <div>
        <div className="lbl">{label}</div>
        <div className="val">
          <span>{value}</span>
        </div>
        {sub && <div className="sub">{sub}</div>}
      </div>
    </div>
  );
}

/**
 * Billing differentiation panel: tells trial/comp access apart from a paid
 * subscription, and (for paid) what the client is liable for (plan list price)
 * vs what they actually pay after coupons. Coupons aren't stored, so the gap
 * between liability and "currently paying" IS the discount.
 */
export default function BillingSummary({ billing }: { billing: Billing }) {
  const meta = TYPE_META[billing.billingType] || TYPE_META.none;
  const sub = billing.subscription;
  const isTrial = billing.billingType === 'trial';
  const discountPerMonth = Math.max(0, (billing.liability || 0) - (billing.currentlyPaying || 0));
  const isDiscounted = billing.billingType === 'discounted';

  return (
    <section className="section">
      <div className="section-head">
        <div>
          <h3>Billing</h3>
          <p className="desc">
            {isTrial
              ? 'On trial / comp access — not billed through Stripe.'
              : 'What this client is liable for (plan list price) vs what they actually pay after any coupon.'}
          </p>
        </div>
        <span className={'status-pill ' + meta.tone}>
          <span className="dot" />
          {meta.label}
        </span>
      </div>

      {isTrial ? (
        <div className="flags">
          <span className="chip warn">Trial access enabled</span>
          {sub?.trialReason ? <span className="chip">Reason: {sub.trialReason}</span> : null}
          <span className="chip">Lifetime collected: {fmtMoney(billing.revenue?.lifetime || 0)}</span>
        </div>
      ) : (
        <>
          <div className="scorecard">
            <Stat
              label="Liable for"
              value={`${fmtMoney(billing.liability || 0)}/mo`}
              sub={`Plan list price · ${sub?.status || 'no subscription'}`}
            />
            <Stat
              label="Currently paying"
              value={`${fmtMoney(billing.currentlyPaying || 0)}/mo`}
              sub={isDiscounted ? `${fmtMoney(discountPerMonth)}/mo off via coupon` : 'Latest recurring invoice'}
            />
            <Stat label="Lifetime collected" value={fmtMoney(billing.revenue?.lifetime || 0)} sub="Sum of paid invoices" />
          </div>
          {isDiscounted && (
            <div className="flags" style={{ marginTop: 12 }}>
              <span className="chip warn">
                Paying {fmtMoney(billing.currentlyPaying || 0)} of {fmtMoney(billing.liability || 0)} list — a coupon/discount
                is in effect.
              </span>
            </div>
          )}
        </>
      )}
    </section>
  );
}
